import { findNextLevel10Meeting, findNextEventsByQuery, formatCalendarEvent } from './calendarService';

// Helper function to check if a message contains calendar queries
export function detectCalendarQuery(message) {
  // Define keywords and patterns for detecting calendar-related queries
  const calendarKeywords = [
    'calendar', 'schedule', 'agenda', 'appointment', 'meeting',
    'when is', 'upcoming', 'event', 'level 10', 'level10', 'l10'
  ];
  
  // Check if message contains any calendar-related keywords
  const lowercaseMessage = message.toLowerCase();
  return calendarKeywords.some(keyword => lowercaseMessage.includes(keyword));
}

// Check specifically for Level 10 meeting queries
export function detectLevel10Query(message) {
  const level10Keywords = ['level 10', 'level10', 'l10 meeting', 'l10'];
  const queryPatterns = [
    'when is', 'next', 'upcoming', 'scheduled', 'planned',
    'do i have', 'is there', 'tell me about'
  ];
  
  const lowercaseMessage = message.toLowerCase();
  
  // Check if message contains Level 10 keywords AND a query pattern
  const hasLevel10Keyword = level10Keywords.some(keyword => 
    lowercaseMessage.includes(keyword)
  );
  
  const hasQueryPattern = queryPatterns.some(pattern => 
    lowercaseMessage.includes(pattern)
  );
  
  return hasLevel10Keyword && hasQueryPattern;
}

// Process calendar requests based on user message
export async function processCalendarRequest(message, companyId) {
  try {
    if (!companyId) {
      return {
        success: false,
        message: "Calendar is not connected. Please connect your Google Calendar in Settings."
      };
    }
    
    const lowercaseMessage = message.toLowerCase();
    
    // Check for Level 10 meeting query first
    if (detectLevel10Query(message)) {
      return await getNextLevel10Meeting(companyId);
    }
    
    // Check for general upcoming events
    if (lowercaseMessage.includes('upcoming') || 
        lowercaseMessage.includes('next few') ||
        lowercaseMessage.includes('this week')) {
      return await getUpcomingEvents(companyId);
    }
    
    // Check for specific event queries
    const eventTypes = ['sales', 'marketing', 'team', 'project', 'standup', 'client', 'review'];
    for (const eventType of eventTypes) {
      if (lowercaseMessage.includes(eventType)) {
        return await getSpecificEvents(companyId, eventType);
      }
    }
    
    // Default to upcoming events for generic calendar requests
    return await getUpcomingEvents(companyId);
    
  } catch (error) {
    console.error('Error processing calendar request:', error);
    return {
      success: false,
      message: `Error accessing calendar: ${error.message}`
    };
  }
}

// Get the next Level 10 meeting
async function getNextLevel10Meeting(companyId) {
  try {
    const meeting = await findNextLevel10Meeting(companyId);
    
    if (!meeting) {
      return {
        success: true,
        message: "I couldn't find any upcoming Level 10 meetings on your calendar.",
        data: null
      };
    }
    
    const formattedMeeting = formatCalendarEvent(meeting);
    
    return {
      success: true,
      message: "I found your next Level 10 meeting:",
      data: {
        type: 'level10Meeting',
        meeting: formattedMeeting
      }
    };
  } catch (error) {
    console.error('Error fetching Level 10 meeting:', error);
    return {
      success: false,
      message: `Error fetching Level 10 meeting: ${error.message}`
    };
  }
}

// Get upcoming events
async function getUpcomingEvents(companyId, maxResults = 5) {
  try {
    // Use the generic query function but filter for upcoming events
    const events = await findNextEventsByQuery(companyId, '', maxResults);
    
    if (!events || events.length === 0) {
      return {
        success: true,
        message: "You don't have any upcoming events on your calendar.",
        data: null
      };
    }
    
    const formattedEvents = events.map(formatCalendarEvent).filter(Boolean);
    
    return {
      success: true,
      message: `Here are your next ${formattedEvents.length} upcoming events:`,
      data: {
        type: 'upcomingEvents',
        events: formattedEvents
      }
    };
  } catch (error) {
    console.error('Error fetching upcoming events:', error);
    return {
      success: false,
      message: `Error fetching upcoming events: ${error.message}`
    };
  }
}

// Get specific event types
async function getSpecificEvents(companyId, eventType, maxResults = 3) {
  try {
    const events = await findNextEventsByQuery(companyId, eventType, maxResults);
    
    if (!events || events.length === 0) {
      return {
        success: true,
        message: `I couldn't find any upcoming ${eventType} events on your calendar.`,
        data: null
      };
    }
    
    const formattedEvents = events.map(formatCalendarEvent).filter(Boolean);
    
    return {
      success: true,
      message: `Here are your upcoming ${eventType} events:`,
      data: {
        type: 'specificEvents',
        eventType,
        events: formattedEvents
      }
    };
  } catch (error) {
    console.error(`Error fetching ${eventType} events:`, error);
    return {
      success: false,
      message: `Error fetching ${eventType} events: ${error.message}`
    };
  }
} 